import { useTranslation } from 'react-i18next';
import { useUserPreferences } from '@/hooks/usePreferences';
import {
  useSetDishPreference,
  useRemoveDishPreference,
  useSetIngredientPreference,
  useRemoveIngredientPreference,
} from '@/hooks/usePreferences';
import { useDishes } from '@/hooks/useDishes';
import { useProducts } from '@/hooks/useProducts';
import { Loading } from '@/components/ui/Loading';
import { useToast } from '@/components/ui/ToastProvider';

interface MemberPreferencesProps {
  userId: number;
}

const DISH_VALUES = ['like', 'neutral', 'dislike'] as const;
const INGREDIENT_VALUES = [
  'like',
  'neutral',
  'avoid',
  'intolerance',
] as const;

export function MemberPreferences({ userId }: MemberPreferencesProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const { data: preferences, isLoading } = useUserPreferences(userId);
  const { data: dishesData } = useDishes();
  const { data: productsData } = useProducts({ limit: 100 });

  const setDish = useSetDishPreference();
  const removeDish = useRemoveDishPreference();
  const setIngredient = useSetIngredientPreference();
  const removeIngredient = useRemoveIngredientPreference();

  const dishPrefs = new Map(
    (preferences?.dishes ?? []).map((p) => [p.dishId, p.value]),
  );
  const ingredientPrefs = new Map(
    (preferences?.ingredients ?? []).map((p) => [p.productId, p.value]),
  );

  const handleDishChange = async (dishId: number, value: string) => {
    try {
      if (value === '') {
        await removeDish.mutateAsync({ dishId, userId });
      } else {
        await setDish.mutateAsync({
          dishId,
          data: { userId, value: value as (typeof DISH_VALUES)[number] },
        });
      }
    } catch {
      toast.error(t('preferences.errorSaving'));
    }
  };

  const handleIngredientChange = async (productId: number, value: string) => {
    try {
      if (value === '') {
        await removeIngredient.mutateAsync({ productId, userId });
      } else {
        await setIngredient.mutateAsync({
          productId,
          data: {
            userId,
            value: value as (typeof INGREDIENT_VALUES)[number],
          },
        });
      }
    } catch {
      toast.error(t('preferences.errorSaving'));
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      {/* Dish preferences */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">
          {t('preferences.dishesTitle')}
        </h2>
        {(dishesData?.dishes ?? []).length === 0 ? (
          <p className="text-sm text-gray-500">{t('preferences.noDishes')}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {dishesData!.dishes.map((dish) => (
              <li
                key={dish.id}
                className="flex items-center justify-between py-2"
              >
                <span className="text-sm text-gray-900">{dish.name}</span>
                <select
                  value={dishPrefs.get(dish.id) ?? ''}
                  onChange={(e) => handleDishChange(dish.id, e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                >
                  <option value="">{t('preferences.none')}</option>
                  {DISH_VALUES.map((value) => (
                    <option key={value} value={value}>
                      {t(`preference.dish.${value}`)}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Ingredient preferences */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">
          {t('preferences.ingredientsTitle')}
        </h2>
        {(productsData?.products ?? []).length === 0 ? (
          <p className="text-sm text-gray-500">
            {t('preferences.noIngredients')}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {productsData!.products.map((product) => (
              <li
                key={product.id}
                className="flex items-center justify-between py-2"
              >
                <span className="text-sm text-gray-900">
                  {product.name}
                  {product.brand ? (
                    <span className="text-gray-400"> — {product.brand}</span>
                  ) : null}
                </span>
                <select
                  value={ingredientPrefs.get(product.id) ?? ''}
                  onChange={(e) =>
                    handleIngredientChange(product.id, e.target.value)
                  }
                  className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                >
                  <option value="">{t('preferences.none')}</option>
                  {INGREDIENT_VALUES.map((value) => (
                    <option key={value} value={value}>
                      {t(`preference.ingredient.${value}`)}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
